import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Divider,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PlayArrow as TestIcon,
  SmartToy as AIIcon,
  Psychology as PsychologyIcon,
  Description as DocumentIcon,
  Chat as ChatIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// Tipos
interface AIProvider {
  id?: number;
  name: string;
  display_name: string;
  is_active: boolean;
  has_api_key: boolean;
  default_model?: string;
  available_models?: string[];
  from_database: boolean;
}

interface AIFlowConfig {
  id?: number;
  flow_name: string;
  display_name: string;
  provider_name: string;
  model_name?: string;
  is_active: boolean;
  from_database: boolean;
}

// Ícones por fluxo
const flowIcons: { [key: string]: React.ReactNode } = {
  chat: <ChatIcon />,
  document_generation: <DocumentIcon />,
  document_improvement: <EditIcon />,
  jurisprudence_search: <SearchIcon />,
};

const AISettingsPage: React.FC = () => {
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [flows, setFlows] = useState<AIFlowConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Estado do diálogo de edição de provider
  const [editProviderDialog, setEditProviderDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<AIProvider | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Estado do diálogo de edição de fluxo
  const [editFlowDialog, setEditFlowDialog] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState<AIFlowConfig | null>(null);
  const [flowProvider, setFlowProvider] = useState('');
  const [flowModel, setFlowModel] = useState('');

  // Carregar dados
  const loadData = async () => {
    setLoading(true);
    try {
      const [providersRes, flowsRes] = await Promise.all([
        api.get('/admin/ai/providers'),
        api.get('/admin/ai/flows'),
      ]);
      setProviders(providersRes.data);
      setFlows(flowsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Abrir diálogo de edição de provider
  const handleEditProvider = (provider: AIProvider) => {
    setSelectedProvider(provider);
    setApiKey('');
    setShowApiKey(false);
    setTestResult(null);
    setEditProviderDialog(true);
  };

  // Testar conexão com provider
  const handleTestProvider = async () => {
    if (!selectedProvider || !apiKey) return;

    setTesting(true);
    setTestResult(null);

    try {
      const response = await api.post('/admin/ai/providers/test', {
        provider_name: selectedProvider.name,
        api_key: apiKey,
        model: selectedProvider.default_model,
      });
      setTestResult(response.data);
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Erro ao testar conexão',
      });
    } finally {
      setTesting(false);
    }
  };

  // Salvar provider
  const handleSaveProvider = async () => {
    if (!selectedProvider) return;

    try {
      await api.patch(`/admin/ai/providers/${selectedProvider.name}`, {
        api_key: apiKey || undefined,
        is_active: true,
      });
      setSuccess(`Provider ${selectedProvider.display_name} atualizado com sucesso!`);
      setEditProviderDialog(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao salvar provider');
    }
  };

  // Toggle ativar/desativar provider
  const handleToggleProvider = async (provider: AIProvider) => {
    try {
      await api.patch(`/admin/ai/providers/${provider.name}`, {
        is_active: !provider.is_active,
      });
      setSuccess(`Provider ${provider.display_name} ${!provider.is_active ? 'ativado' : 'desativado'} com sucesso!`);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao alterar status do provider');
    }
  };

  // Abrir diálogo de edição de fluxo
  const handleEditFlow = (flow: AIFlowConfig) => {
    setSelectedFlow(flow);
    setFlowProvider(flow.provider_name);
    setFlowModel(flow.model_name || '');
    setEditFlowDialog(true);
  };

  // Salvar fluxo
  const handleSaveFlow = async () => {
    if (!selectedFlow) return;

    try {
      await api.patch(`/admin/ai/flows/${selectedFlow.flow_name}`, {
        provider_name: flowProvider,
        model_name: flowModel || null,
      });
      setSuccess(`Fluxo ${selectedFlow.display_name} atualizado com sucesso!`);
      setEditFlowDialog(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao salvar fluxo');
    }
  };

  // Obter modelos do provider selecionado
  const getModelsForProvider = (providerName: string): string[] => {
    const provider = providers.find((p) => p.name === providerName);
    return provider?.available_models || [];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PsychologyIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Configurações de IA
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gerencie provedores de IA e configure qual usar em cada fluxo
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Recarregar configurações">
          <IconButton onClick={loadData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Provedores de IA */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon />
          Provedores de IA
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure as chaves de API dos provedores que deseja utilizar
        </Typography>

        <Grid container spacing={3}>
          {providers.map((provider) => (
            <Grid item xs={12} md={4} key={provider.name}>
              <Card
                sx={{
                  height: '100%',
                  borderLeft: provider.has_api_key && provider.is_active ? '4px solid' : 'none',
                  borderColor: 'success.main',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">{provider.display_name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={provider.is_active ? 'Clique para desativar' : 'Clique para ativar'}>
                        <Switch
                          size="small"
                          checked={provider.is_active && provider.has_api_key}
                          onChange={() => handleToggleProvider(provider)}
                          disabled={!provider.has_api_key}
                          color="success"
                        />
                      </Tooltip>
                      <Chip
                        size="small"
                        label={provider.has_api_key && provider.is_active ? 'Ativo' : 'Inativo'}
                        color={provider.has_api_key && provider.is_active ? 'success' : 'default'}
                      />
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Modelo padrão: <strong>{provider.default_model || 'Não definido'}</strong>
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Chave API: {provider.has_api_key ? '••••••••' : 'Não configurada'}
                  </Typography>

                  {provider.available_models && provider.available_models.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Modelos disponíveis:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {provider.available_models.slice(0, 3).map((model) => (
                          <Chip key={model} label={model} size="small" variant="outlined" />
                        ))}
                        {provider.available_models.length > 3 && (
                          <Chip label={`+${provider.available_models.length - 3}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditProvider(provider)}
                  >
                    Configurar
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Configuração de Fluxos */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          Configuração de Fluxos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Defina qual provedor de IA usar em cada funcionalidade do sistema
        </Typography>

        <Grid container spacing={3}>
          {flows.map((flow) => {
            const provider = providers.find((p) => p.name === flow.provider_name);
            return (
              <Grid item xs={12} md={6} key={flow.flow_name}>
                <Card
                  sx={{
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: 'primary.light',
                          color: 'primary.contrastText',
                        }}
                      >
                        {flowIcons[flow.flow_name] || <AIIcon />}
                      </Box>
                      <Box>
                        <Typography variant="h6">{flow.display_name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {flow.flow_name}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Provider:
                        </Typography>
                        <Chip
                          label={provider?.display_name || flow.provider_name}
                          color={provider?.has_api_key && provider?.is_active ? 'primary' : 'default'}
                          size="small"
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Modelo:
                        </Typography>
                        <Typography variant="body2">
                          {flow.model_name || provider?.default_model || 'Padrão'}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditFlow(flow)}
                    >
                      Alterar
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Dialog de Edição de Provider */}
      <Dialog open={editProviderDialog} onClose={() => setEditProviderDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Configurar {selectedProvider?.display_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Chave da API"
              type={showApiKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={selectedProvider?.has_api_key ? '••••••••••••••••' : 'Cole a chave da API aqui'}
              helperText={selectedProvider?.has_api_key ? 'Deixe em branco para manter a chave atual' : 'Obrigatório para ativar o provider'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowApiKey(!showApiKey)} edge="end">
                      {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Button
              variant="outlined"
              startIcon={testing ? <CircularProgress size={16} /> : <TestIcon />}
              onClick={handleTestProvider}
              disabled={!apiKey || testing}
              fullWidth
              sx={{ mb: 2 }}
            >
              {testing ? 'Testando conexão...' : 'Testar Conexão'}
            </Button>

            {testResult && (
              <Alert
                severity={testResult.success ? 'success' : 'error'}
                sx={{ mt: 2 }}
              >
                {testResult.message}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProviderDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveProvider}
            disabled={!selectedProvider?.has_api_key && !apiKey}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Edição de Fluxo */}
      <Dialog open={editFlowDialog} onClose={() => setEditFlowDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Configurar {selectedFlow?.display_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Provedor de IA</InputLabel>
              <Select
                value={flowProvider}
                label="Provedor de IA"
                onChange={(e) => {
                  setFlowProvider(e.target.value);
                  setFlowModel('');
                }}
              >
                {providers
                  .filter((p) => p.has_api_key && p.is_active)
                  .map((provider) => (
                    <MenuItem key={provider.name} value={provider.name}>
                      {provider.display_name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Modelo</InputLabel>
              <Select
                value={flowModel}
                label="Modelo"
                onChange={(e) => setFlowModel(e.target.value)}
              >
                <MenuItem value="">
                  <em>Usar padrão do provider</em>
                </MenuItem>
                {getModelsForProvider(flowProvider).map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!providers.some((p) => p.has_api_key && p.is_active) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Nenhum provedor de IA está configurado. Configure pelo menos um provider antes de alterar os fluxos.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFlowDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveFlow}
            disabled={!flowProvider}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AISettingsPage;

